/* ============================================================
   IDEAS CAFE — Header behaviour
   Sticky detection, mobile drawer, live cart count.

   Also installs window.IdeasCart, the shared cart contract every
   other section uses:

     IdeasCart.refresh()          re-fetch /cart.js and broadcast
     IdeasCart.get()              last known cart, or null
     IdeasCart.broadcast(cart)    publish a cart you already have

   Broadcasting fires `ideas:cart:updated` on document with
   { detail: { cart } }. Sections that mutate the cart should call
   broadcast() with the response body rather than refresh(), to
   avoid a redundant round trip.
   ============================================================ */
(function () {
  'use strict';

  /* ── Shared cart contract ──────────────────────────────── */

  if (!window.IdeasCart) {
    var state = null;
    var inFlight = null;

    window.IdeasCart = {
      get: function () {
        return state;
      },

      broadcast: function (cart) {
        if (!cart) return;
        state = cart;
        document.dispatchEvent(
          new CustomEvent('ideas:cart:updated', { detail: { cart: cart } })
        );
      },

      refresh: function () {
        // Coalesce concurrent callers onto one request.
        if (inFlight) return inFlight;
        inFlight = fetch(window.Shopify && window.Shopify.routes
            ? window.Shopify.routes.root + 'cart.js'
            : '/cart.js', {
            headers: { Accept: 'application/json' }
          })
          .then(function (res) {
            if (!res.ok) throw new Error('Cart fetch failed: ' + res.status);
            return res.json();
          })
          .then(function (cart) {
            window.IdeasCart.broadcast(cart);
            return cart;
          })
          .catch(function (err) {
            console.warn('[ideas] cart refresh failed', err);
            return null;
          })
          .then(function (cart) {
            inFlight = null;
            return cart;
          });
        return inFlight;
      }
    };
  }

  /* ── Header element ────────────────────────────────────── */

  if (customElements.get('ideas-header')) return;

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

  class IdeasHeader extends HTMLElement {
    connectedCallback() {
      this.drawer = this.querySelector('[id="IdeasHeaderDrawer"]');
      this.scrim = this.querySelector('.ideas-header__scrim');
      this.burger = this.querySelector('[data-drawer-open]');
      this.countEls = this.querySelectorAll('[data-cart-count]');
      this.countA11y = this.querySelectorAll('[data-cart-count-a11y]');

      this._onCart = this._onCart.bind(this);
      this._onKeydown = this._onKeydown.bind(this);

      this._bindDrawer();
      this._bindSticky();

      document.addEventListener('ideas:cart:updated', this._onCart);

      // A cached back-forward navigation can restore a stale count.
      this._onPageShow = function (e) {
        if (e.persisted) window.IdeasCart.refresh();
      };
      window.addEventListener('pageshow', this._onPageShow);
    }

    disconnectedCallback() {
      document.removeEventListener('ideas:cart:updated', this._onCart);
      document.removeEventListener('keydown', this._onKeydown);
      window.removeEventListener('pageshow', this._onPageShow);
      if (this._observer) this._observer.disconnect();
      document.body.classList.remove('ideas-drawer-open');
    }

    /* ── Cart count ──────────────────────────────────────── */

    _onCart(event) {
      var cart = event.detail && event.detail.cart;
      if (!cart) return;
      var count = cart.item_count;

      this.countEls.forEach(function (el) {
        if (el.textContent.trim() === String(count)) return;
        el.textContent = count;
        el.classList.remove('is-bumped');
        // Reflow so the animation restarts on a repeat change.
        void el.offsetWidth;
        el.classList.add('is-bumped');
      });

      this.countA11y.forEach(function (el) {
        el.textContent = 'Cart, ' + count;
      });
    }

    /* ── Sticky shadow ───────────────────────────────────── */

    _bindSticky() {
      if (this.dataset.sticky !== 'true') return;

      /* A sentinel in normal flow directly above the header: when it
         scrolls out of view the header has detached from the top.
         Cheaper and smoother than a scroll listener.

         It goes before the SECTION WRAPPER, which is the element that
         actually sticks, and stays in the flow so it marks the header's
         resting position. Previously it was absolutely positioned at
         top:0 of the page, which meant it left the viewport on the first
         pixel of scroll — the shadow appeared while the header was still
         moving, and with an announcement bar above it fired far too
         early. The negative margin cancels its own height. */
      var anchor = this.closest('.shopify-section') || this;
      var sentinel = document.createElement('div');
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.cssText = 'height:1px;margin-bottom:-1px;pointer-events:none;';
      anchor.parentNode.insertBefore(sentinel, anchor);

      var self = this;
      this._observer = new IntersectionObserver(
        function (entries) {
          self.classList.toggle('is-stuck', !entries[0].isIntersecting);
        },
        { rootMargin: '0px', threshold: 0 }
      );
      this._observer.observe(sentinel);
    }

    /* ── Drawer ──────────────────────────────────────────── */

    _bindDrawer() {
      if (!this.drawer) return;
      var self = this;

      this.querySelectorAll('[data-drawer-open]').forEach(function (el) {
        el.addEventListener('click', function () {
          self.openDrawer();
        });
      });

      this.querySelectorAll('[data-drawer-close]').forEach(function (el) {
        el.addEventListener('click', function () {
          self.closeDrawer();
        });
      });
    }

    openDrawer() {
      if (!this.drawer || this._open) return;
      this._open = true;
      this._lastFocus = document.activeElement;

      this.drawer.hidden = false;
      if (this.scrim) this.scrim.hidden = false;

      // Next frame, so the transition has a from-state to animate.
      var self = this;
      requestAnimationFrame(function () {
        self.drawer.classList.add('is-open');
        if (self.scrim) self.scrim.classList.add('is-open');
      });

      document.body.classList.add('ideas-drawer-open');
      if (this.burger) this.burger.setAttribute('aria-expanded', 'true');
      document.addEventListener('keydown', this._onKeydown);

      var first = this.drawer.querySelector(FOCUSABLE);
      if (first) first.focus();
    }

    closeDrawer() {
      if (!this.drawer || !this._open) return;
      this._open = false;

      this.drawer.classList.remove('is-open');
      if (this.scrim) this.scrim.classList.remove('is-open');

      document.body.classList.remove('ideas-drawer-open');
      if (this.burger) this.burger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('keydown', this._onKeydown);

      var self = this;
      var hide = function () {
        if (self._open) return;
        self.drawer.hidden = true;
        if (self.scrim) self.scrim.hidden = true;
      };

      // Wait out the slide, but never strand the panel visible if the
      // transition is suppressed (reduced motion, background tab).
      var done = false;
      var onEnd = function () {
        if (done) return;
        done = true;
        self.drawer.removeEventListener('transitionend', onEnd);
        hide();
      };
      this.drawer.addEventListener('transitionend', onEnd);
      setTimeout(onEnd, 400);

      if (this._lastFocus && this._lastFocus.focus) this._lastFocus.focus();
    }

    _onKeydown(event) {
      if (event.key === 'Escape') {
        this.closeDrawer();
        return;
      }
      if (event.key !== 'Tab' || !this._open) return;

      var items = Array.prototype.filter.call(
        this.drawer.querySelectorAll(FOCUSABLE),
        function (el) {
          return el.offsetParent !== null;
        }
      );
      if (!items.length) return;

      var first = items[0];
      var last = items[items.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('ideas-header', IdeasHeader);
})();
